// @flow
import React from 'react';
import {useIntl, defineMessages, FormattedMessage} from 'react-intl';
import {TextInput} from 'sail/Input';
import Group from 'sail/Group';
import Box from 'sail/Box';
import {CaptionMono} from 'sail/Text';
import {HeaderBox, GridBox, dimensions, GridRow} from './StyleHelpers';
import type {Commands} from '../StripeClui/helpers/commands/resource';
import {ArrayInputBase} from './inputs/arrayInputComponents/ArrayInputBase';
import {BooleanInput} from './inputs/BooleanInput';
import {StringInput} from './inputs/StringInput';
import {IntegerInput} from './inputs/IntegerInput';
import {NumberInput} from './inputs/NumberInput';
import {EnumInput} from './inputs/EnumInput';
import {useApiExplorer} from './ApiExplorerProvider';
import {lookUpResourceByName} from './resources';

type ParamsProps = {|
  resources: Commands,
|};

const messages = defineMessages({
  pathArgs: {
    id: 'docsApp.Params.pathArgs',
    description: 'Title for path arguments form block',
    defaultMessage: 'Path Arguments',
  },
  required: {
    id: 'docsApp.Params.required',
    description: 'Label for required fields',
    defaultMessage: 'Required parameter',
  },
  optional: {
    id: 'docsApp.Params.optional',
    description: 'Label for optional fields',
    defaultMessage: 'Optional parameter',
  },
  value: {
    id: 'docsApp.Params.value',
    description: 'Label for value inputs',
    defaultMessage: 'Value',
  },
});

const handlePathArgUpdate = (value, arg, pathArgs, setPathArgs) => {
  if (value) {
    setPathArgs({...pathArgs, [arg]: value});
  } else {
    const {[arg]: removed, ...newPathArgs} = pathArgs;
    setPathArgs(newPathArgs);
  }
};

const renderPathArgs = (args, pathArgs, setPathArgs, intl) => {
  return (
    <GridBox>
      {args.map((arg, ind) => {
        const field_id = `${arg}_input`;
        return (
          <GridRow required label={arg} field_id={field_id} row={ind + 1}>
            <TextInput
              id={field_id}
              name={field_id}
              onChange={(value) =>
                handlePathArgUpdate(value, arg, pathArgs, setPathArgs)
              }
              value={arg in pathArgs ? pathArgs[arg] : ''}
              required
              style={{height: dimensions.height}}
              width={dimensions.width}
              radius="none"
              end={{
                'aria-label': 'string',
                content: <CaptionMono color="gray">{'string'}</CaptionMono>,
              }}
              autoFocus={false}
            />
          </GridRow>
        );
      })}
    </GridBox>
  );
};

export const Params = ({resources}: ParamsProps) => {
  const intl = useIntl();
  const {
    state: {resource, action, params, pathArgs},
    setParams,
    setPathArgs,
  } = useApiExplorer();
  const command = lookUpResourceByName(resources, resource)?.[action];

  if (!command?.params) {
    return null;
  }

  const paramMap = Object.keys(command.params).reduce((acc, param_name) => {
    acc[param_name.replace(/-/g, '_')] = command.params?.[param_name];
    return acc;
  }, {});

  // Get the arg
  const args =
    command?.args?.length && command.args.length > 0 ? command.args : [];

  // Prepare list of param objects for rendering (required fields at the beginning)
  const paramsHelper = Object.entries(paramMap)
    .filter(([_, param]) => {
      return typeof param === 'object' && param?.input_type;
    })
    .map(([param_key, param]) => {
      const field_id = `${param_key}_input`;
      const required: boolean = !!(
        typeof param === 'object' && param?.required
      );
      const description =
        typeof param === 'object' && param?.description
          ? intl.formatMessage({
              id: `docsApp.Params.${param_key}-description`,
              defaultMessage: param.description,
            })
          : '';

      return {param_key, field_id, required, description, param};
    });

  const getParamInputs = (paramsList) =>
    paramsList.map(
      ({param_key, field_id, required, description, param}, ind) => {
        if (
          !(typeof param === 'object' && typeof param?.input_type === 'object')
        ) {
          return null;
        }

        const row = ind + 1;

        if (
          typeof param?.input_type === 'object' &&
          param?.input_type?.shape === 'primitive'
        ) {
          const inputType = param.input_type?.primitive
            ? param.input_type.primitive
            : '';
          switch (inputType) {
            case 'integer':
            case 'DateTime':
              // TODO: For DateTime, for now accept a numerical input
              // (represents a Unix timestamp in the request),
              // but probably want to convert to a more intuitive input like the one in
              // manage/frontend/src/platform/components/DateTimePicker/DateTimePicker.js
              return (
                <GridRow
                  required={required}
                  label={param_key}
                  field_id={field_id}
                  row={row}
                  description={description}
                >
                  <IntegerInput
                    field_id={field_id}
                    param_key={param_key}
                    required={required}
                    inputType={inputType}
                    params={params}
                    setParams={setParams}
                    key={field_id}
                  />
                </GridRow>
              );
            case 'boolean':
              return (
                <GridRow
                  required={required}
                  label={param_key}
                  field_id={field_id}
                  row={row}
                  description={description}
                >
                  <BooleanInput
                    field_id={field_id}
                    param_key={param_key}
                    required={required}
                    inputType={inputType}
                    params={params}
                    setParams={setParams}
                    key={field_id}
                  />
                </GridRow>
              );
            case 'number':
              return (
                <GridRow
                  required={required}
                  label={param_key}
                  field_id={field_id}
                  row={row}
                  description={description}
                >
                  <NumberInput
                    field_id={field_id}
                    param_key={param_key}
                    required={required}
                    inputType={inputType}
                    params={params}
                    setParams={setParams}
                    key={field_id}
                  />
                </GridRow>
              );
            case 'decimal_string': // TODO: validate that input looks like a decimal number
            case 'string':
              return (
                <GridRow
                  required={required}
                  label={param_key}
                  field_id={field_id}
                  row={row}
                  description={description}
                >
                  <StringInput
                    field_id={field_id}
                    param_key={param_key}
                    required={required}
                    inputType={inputType}
                    params={params}
                    setParams={setParams}
                    key={field_id}
                  />
                </GridRow>
              );
          }
        } else if (param.input_type?.shape === 'enum') {
          return (
            <GridRow
              required={required}
              label={param_key}
              field_id={field_id}
              row={row}
              description={description}
            >
              <EnumInput
                field_id={field_id}
                param_key={param_key}
                required={required}
                inputType="enum"
                param={param}
                params={params}
                setParams={setParams}
                key={field_id}
              />
            </GridRow>
          );
        } else if (param.input_type?.shape === 'array') {
          return (
            <GridRow
              required={required}
              label={param_key}
              field_id={field_id}
              row={row}
              description={description}
            >
              <ArrayInputBase
                field_id={field_id}
                param_key={param_key}
                required={required}
                param={param}
                key={field_id}
              />
            </GridRow>
          );
        }
        // default is string input
        return (
          <GridRow
            required={required}
            label={param_key}
            field_id={field_id}
            row={row}
            description={description}
          >
            <StringInput
              field_id={field_id}
              param_key={param_key}
              required={required}
              inputType="not supported" // TODO: support all input types
              params={params}
              setParams={setParams}
              key={field_id}
            />
          </GridRow>
        );
      },
    );

  const requiredParams = getParamInputs(
    paramsHelper.filter((param) => param.required),
  );
  const optionalParams = getParamInputs(
    paramsHelper.filter((param) => !param.required),
  );

  return (
    <Group direction="vertical" spacing={12}>
      {args.length > 0 ? (
        <Box flex={{direction: 'column'}}>
          <Box flex={{direction: 'row'}} padding={{vertical: 12}}>
            <HeaderBox>
              <FormattedMessage {...messages.pathArgs} />
            </HeaderBox>
            <HeaderBox>
              <FormattedMessage {...messages.value} />
            </HeaderBox>
          </Box>
          {renderPathArgs(args, pathArgs, setPathArgs, intl)}
        </Box>
      ) : null}
      {requiredParams.length > 0 ? (
        <Box flex={{direction: 'column'}}>
          <Box flex={{direction: 'row'}} padding={{vertical: 12}}>
            <HeaderBox>
              <FormattedMessage {...messages.required} />
            </HeaderBox>
            <HeaderBox>
              <FormattedMessage {...messages.value} />
            </HeaderBox>
          </Box>
          <GridBox>{requiredParams}</GridBox>
        </Box>
      ) : null}
      {optionalParams.length > 0 ? (
        <Box flex={{direction: 'column'}}>
          <Box flex={{direction: 'row'}} padding={{vertical: 12}}>
            <HeaderBox>
              <FormattedMessage {...messages.optional} />
            </HeaderBox>
            <HeaderBox>
              <FormattedMessage {...messages.value} />
            </HeaderBox>
          </Box>
          <GridBox>{optionalParams}</GridBox>
        </Box>
      ) : null}
    </Group>
  );
};
